import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, Shield, ShieldAlert, Bomb, ShieldQuestion, MessageSquareWarning, Award, TrendingUp } from 'lucide-react';
import type {
  VendorBattlecard,
  VerticalDifferentiator,
  BattlecardStrength,
  BattlecardWeakness,
  BattlecardLandmine,
  BattlecardCompetitorLandmine,
  BattlecardObjection,
  BattlecardProofPoint,
} from '../../types';
import FormattedText from '../common/FormattedText';

interface Props {
  battlecard: VendorBattlecard;
  differentiators: VerticalDifferentiator[];
  competitorName: string;
}

const RISK_COLORS = {
  high: 'bg-app-critical/10 text-app-critical',
  medium: 'bg-app-warning/10 text-app-warning',
  low: 'bg-app-positive/10 text-app-positive',
};

const PROOF_TYPE_COLORS = {
  analyst: 'bg-app-focus/10 text-app-focus border-app-focus',
  customer: 'bg-app-positive/10 text-app-positive border-app-positive',
  benchmark: 'bg-app-warning/10 text-app-warning border-app-warning',
  architectural: 'bg-app-canvas text-app-ink border-app-line',
};

const IMPACT_COLORS = {
  risk: { bg: 'bg-app-critical/10', text: 'text-app-critical', border: 'border-app-critical', badge: 'bg-app-critical/10 text-app-critical' },
  revenue: { bg: 'bg-app-positive/10', text: 'text-app-positive', border: 'border-app-positive', badge: 'bg-app-positive/10 text-app-positive' },
  cost: { bg: 'bg-app-focus/10', text: 'text-app-focus', border: 'border-app-focus', badge: 'bg-app-focus/10 text-app-focus' },
};

export default function BattlecardView({ battlecard, differentiators, competitorName }: Props) {
  const [openSection, setOpenSection] = useState<string | null>('strengths');
  const [expandedVertical, setExpandedVertical] = useState<string | null>(null);

  const toggle = (key: string) => setOpenSection(openSection === key ? null : key);

  const strengths = differentiators.filter(d => d.category === 'strength');
  const weaknesses = differentiators.filter(d => d.category === 'weakness');

  const verticalGroups = differentiators.reduce<Record<string, VerticalDifferentiator[]>>((acc, d) => {
    if (!acc[d.vertical_name]) acc[d.vertical_name] = [];
    acc[d.vertical_name].push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {battlecard.executive_summary && (
        <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
          <h3 className="text-base font-semibold text-app-ink mb-2">Head-to-Head Summary</h3>
          <FormattedText text={battlecard.executive_summary} className="text-sm text-app-ink" />
        </div>
      )}

      {battlecard.analyst_perspective && (
        <div className="bg-app-focus/10 rounded-lg border border-app-focus p-6">
          <h3 className="text-base font-semibold text-app-ink mb-2">Analyst Perspective</h3>
          <FormattedText text={battlecard.analyst_perspective} className="text-sm text-app-focus" />
        </div>
      )}

      <Section
        title={`Where We Win (${(battlecard.our_strengths || []).length})`}
        icon={<Shield className="w-4 h-4 text-app-positive" />}
        isOpen={openSection === 'strengths'}
        onToggle={() => toggle('strengths')}
        accentColor="green"
      >
        <div className="space-y-4">
          {(battlecard.our_strengths || []).map((s: BattlecardStrength, i: number) => (
            <div key={i} className="bg-app-positive/10 rounded-lg border border-app-positive p-4 space-y-3">
              <h5 className="text-sm font-semibold text-app-positive">{s.area}</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-app-surface/70 rounded-lg p-3">
                  <p className="text-xs font-medium text-app-positive mb-1">Our Approach</p>
                  <p className="text-xs text-app-ink leading-relaxed">{s.our_approach}</p>
                </div>
                <div className="bg-app-surface/70 rounded-lg p-3">
                  <p className="text-xs font-medium text-app-critical mb-1">Their Gap</p>
                  <p className="text-xs text-app-ink leading-relaxed">{s.competitor_gap}</p>
                </div>
              </div>
              {s.so_what && (
                <div className="bg-app-surface/70 rounded-lg p-3">
                  <p className="text-xs font-medium text-app-muted mb-1">So What?</p>
                  <p className="text-sm text-app-ink leading-relaxed">{s.so_what}</p>
                </div>
              )}
              {s.evidence && (
                <p className="text-xs text-app-positive italic">{s.evidence}</p>
              )}
              {s.talk_track && (
                <div className="bg-app-positive/10 rounded-lg p-3 border border-app-positive">
                  <p className="text-xs font-medium text-app-positive mb-1">Talk Track</p>
                  <p className="text-xs text-app-positive leading-relaxed">{s.talk_track}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section
        title={`Where They Win (${(battlecard.our_weaknesses || []).length})`}
        icon={<ShieldAlert className="w-4 h-4 text-app-warning" />}
        isOpen={openSection === 'weaknesses'}
        onToggle={() => toggle('weaknesses')}
        accentColor="amber"
      >
        <div className="space-y-4">
          {(battlecard.our_weaknesses || []).map((w: BattlecardWeakness, i: number) => (
            <div key={i} className="bg-app-warning/10 rounded-lg border border-app-warning p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <h5 className="text-sm font-semibold text-app-warning">{w.area}</h5>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${RISK_COLORS[w.risk_level] || RISK_COLORS.medium}`}>
                  {(w.risk_level || 'medium').toUpperCase()} RISK
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-app-surface/70 rounded-lg p-3">
                  <p className="text-xs font-medium text-app-warning mb-1">Their Approach</p>
                  <p className="text-xs text-app-ink leading-relaxed">{w.competitor_approach}</p>
                </div>
                <div className="bg-app-surface/70 rounded-lg p-3">
                  <p className="text-xs font-medium text-app-critical mb-1">Our Gap</p>
                  <p className="text-xs text-app-ink leading-relaxed">{w.our_gap}</p>
                </div>
              </div>
              {w.mitigation && (
                <div className="bg-app-surface/70 rounded-lg p-3 border border-app-warning">
                  <p className="text-xs font-medium text-forest-700 mb-1">How to Navigate This</p>
                  <p className="text-xs text-app-ink leading-relaxed">{w.mitigation}</p>
                </div>
              )}
              {w.evidence && (
                <p className="text-xs text-app-warning italic">{w.evidence}</p>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section
        title={`Landmines to Set (${(battlecard.landmines || []).length})`}
        icon={<Bomb className="w-4 h-4 text-forest-600" />}
        isOpen={openSection === 'landmines'}
        onToggle={() => toggle('landmines')}
        accentColor="forest"
      >
        <div className="space-y-3">
          {(battlecard.landmines || []).map((l: BattlecardLandmine, i: number) => (
            <div key={i} className="bg-forest-50 rounded-lg border border-forest-200 p-4 space-y-2">
              <h5 className="text-sm font-semibold text-forest-900">{l.trap}</h5>
              <p className="text-xs text-forest-700 leading-relaxed">{l.why_it_works}</p>
              <p className="text-xs text-app-muted"><span className="font-medium text-app-muted">When to use:</span> {l.when_to_use}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title={`Landmines to Expect (${(battlecard.competitor_landmines || []).length})`}
        icon={<ShieldQuestion className="w-4 h-4 text-app-critical" />}
        isOpen={openSection === 'competitor_landmines'}
        onToggle={() => toggle('competitor_landmines')}
        accentColor="red"
      >
        <div className="space-y-3">
          {(battlecard.competitor_landmines || []).map((l: BattlecardCompetitorLandmine, i: number) => (
            <div key={i} className="bg-app-critical/10 rounded-lg border border-app-critical p-4 space-y-2">
              <h5 className="text-sm font-semibold text-app-critical">{l.trap}</h5>
              <div className="bg-app-surface/70 rounded-lg p-3 space-y-2">
                <div>
                  <p className="text-xs font-medium text-app-muted mb-0.5">How to Counter</p>
                  <p className="text-xs text-app-ink leading-relaxed">{l.how_to_counter}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-app-muted mb-0.5">Reframe</p>
                  <p className="text-xs text-app-ink leading-relaxed">{l.reframe}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title={`Objection Handling (${(battlecard.objection_handling || []).length})`}
        icon={<MessageSquareWarning className="w-4 h-4 text-app-muted" />}
        isOpen={openSection === 'objections'}
        onToggle={() => toggle('objections')}
        accentColor="gray"
      >
        <div className="space-y-3">
          {(battlecard.objection_handling || []).map((o: BattlecardObjection, i: number) => (
            <div key={i} className="bg-app-canvas rounded-lg border border-app-line p-4 space-y-3">
              <div>
                <p className="text-xs font-medium text-app-critical mb-1">Objection</p>
                <p className="text-sm font-medium text-app-ink">{o.objection}</p>
              </div>
              <div className="bg-app-surface rounded-lg p-3">
                <p className="text-xs font-medium text-app-positive mb-1">Response</p>
                <p className="text-xs text-app-ink leading-relaxed">{o.response}</p>
              </div>
              {o.proof_point && (
                <p className="text-xs text-app-muted italic">{o.proof_point}</p>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section
        title={`Proof Points (${(battlecard.proof_points || []).length})`}
        icon={<Award className="w-4 h-4 text-app-focus" />}
        isOpen={openSection === 'proof'}
        onToggle={() => toggle('proof')}
        accentColor="blue"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(battlecard.proof_points || []).map((p: BattlecardProofPoint, i: number) => {
            const colors = PROOF_TYPE_COLORS[p.type] || PROOF_TYPE_COLORS.architectural;
            return (
              <div key={i} className={`rounded-lg border p-4 space-y-2 ${colors}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase">{p.type}</span>
                  {p.source && <span className="text-xs opacity-75">{p.source}</span>}
                </div>
                <p className="text-sm leading-relaxed">{p.statement}</p>
                {p.use_when && (
                  <p className="text-xs opacity-75">Use when: {p.use_when}</p>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {Object.keys(verticalGroups).length > 0 && (
        <Section
          title={`Vertical Analysis (${Object.keys(verticalGroups).length})`}
          icon={<TrendingUp className="w-4 h-4 text-forest-600" />}
          isOpen={openSection === 'verticals'}
          onToggle={() => toggle('verticals')}
          accentColor="forest"
        >
          <div className="space-y-4">
            {Object.entries(verticalGroups).map(([vertical, diffs]) => {
              const vStrengths = diffs.filter(d => d.category === 'strength');
              const vWeaknesses = diffs.filter(d => d.category === 'weakness');
              const isExpanded = expandedVertical === vertical;

              return (
                <div key={vertical} className="bg-app-canvas rounded-lg border border-app-line overflow-hidden">
                  <button
                    onClick={() => setExpandedVertical(isExpanded ? null : vertical)}
                    className="w-full flex items-center justify-between p-4 hover:bg-app-canvas transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <h5 className="text-sm font-semibold text-app-ink">{vertical}</h5>
                      <div className="flex items-center gap-2">
                        {vStrengths.length > 0 && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-app-positive/10 text-app-positive">
                            {vStrengths.length} win{vStrengths.length > 1 ? 's' : ''}
                          </span>
                        )}
                        {vWeaknesses.length > 0 && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-app-warning/10 text-app-warning">
                            {vWeaknesses.length} gap{vWeaknesses.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-app-muted" /> : <ChevronDown className="w-4 h-4 text-app-muted" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3">
                      {diffs.map((d, i) => {
                        const isStrength = d.category === 'strength';
                        const colors = IMPACT_COLORS[d.impact_type as keyof typeof IMPACT_COLORS] || IMPACT_COLORS.revenue;
                        return (
                          <div key={i} className={`rounded-lg border p-4 space-y-3 ${isStrength ? 'bg-app-positive/10 border-app-positive' : 'bg-app-warning/10 border-app-warning'}`}>
                            <div className="flex items-start justify-between gap-3">
                              <p className={`text-sm font-medium ${isStrength ? 'text-app-positive' : 'text-app-warning'}`}>{d.differentiator}</p>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isStrength ? 'bg-app-positive/10 text-app-positive' : 'bg-app-warning/10 text-app-warning'}`}>
                                  {isStrength ? 'WIN' : 'GAP'}
                                </span>
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
                                  {d.impact_type.toUpperCase()}
                                </span>
                              </div>
                            </div>
                            {(d.our_approach || d.competitor_approach) && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {d.our_approach && (
                                  <div className="bg-app-surface/70 rounded-lg p-2.5">
                                    <p className="text-xs font-medium text-app-positive mb-0.5">Our Approach</p>
                                    <p className="text-xs text-app-ink leading-relaxed">{d.our_approach}</p>
                                  </div>
                                )}
                                {d.competitor_approach && (
                                  <div className="bg-app-surface/70 rounded-lg p-2.5">
                                    <p className="text-xs font-medium text-app-warning mb-0.5">{competitorName}'s Approach</p>
                                    <p className="text-xs text-app-ink leading-relaxed">{d.competitor_approach}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="bg-app-surface/70 rounded-lg p-2.5">
                              <p className="text-xs font-medium text-app-muted mb-0.5">So What?</p>
                              <p className="text-xs text-app-ink leading-relaxed">{d.so_what_statement}</p>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-app-muted">
                              {d.resonates_with_persona && (
                                <span>Persona: <span className="font-medium text-app-ink">{d.resonates_with_persona}</span></span>
                              )}
                            </div>
                            {d.evidence && (
                              <p className="text-xs italic text-app-muted">{d.evidence}</p>
                            )}
                            {d.narrative_focus && (
                              <div className={`rounded-lg p-2.5 border ${isStrength ? 'bg-app-positive/10 border-app-positive' : 'bg-app-warning/10 border-app-warning'}`}>
                                <p className="text-xs font-medium text-app-muted mb-0.5">Narrative Focus</p>
                                <p className="text-xs text-app-ink leading-relaxed">{d.narrative_focus}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, icon, isOpen, onToggle, accentColor, children }: {
  title: string;
  icon: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  accentColor: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-app-surface rounded-lg border border-app-line shadow-none overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-5 hover:bg-app-canvas transition-colors">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 bg-${accentColor}-50 rounded-lg flex items-center justify-center`}>
            {icon}
          </div>
          <h4 className="text-sm font-semibold text-app-ink">{title}</h4>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-app-muted" /> : <ChevronDown className="w-4 h-4 text-app-muted" />}
      </button>
      {isOpen && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}
